import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ApiEntityStatistics } from '../../../../api/hexa/ApiEntityStatistics';
import {
  AVAILABLE_STATISTICS_ORIGINS,
  formatEntityLabel,
} from '../../../../api/hexa/models/EntityStatisticsModels';
import {
  DateRangeFilter,
  EntityIdFilter,
  OriginFilter,
  ResultsCountFilter,
} from '../StatisticsFilters';
import StatisticsRankedList from '../StatisticsRankedList';
import StatisticsSection from '../StatisticsSection';
import TooltipLink, { API_DOCS_URLS } from '../TooltipLink';
import { useMostSearchedStats } from '../useMostSearchedStats';

interface EntitiesTabProps {
  onFetchError: (message: string) => void;
}

const EntitiesTab = ({ onFetchError }: EntitiesTabProps) => {
  const { t } = useTranslation();

  const fetchEntities = useCallback(
    (params: Parameters<typeof ApiEntityStatistics.mostReturnedEntities>[0]) =>
      ApiEntityStatistics.mostReturnedEntities(params).then(
        (response) => response.most_returned
      ),
    []
  );

  const entitiesStats = useMostSearchedStats(fetchEntities, onFetchError);

  return (
    <StatisticsSection
      title={t('entity_statistics.entities_title')}
      subtitle={t('entity_statistics.entities_subtitle')}
      subtitleTooltip={
        <Trans
          i18nKey="entity_statistics.entities_subtitle_tooltip"
          components={{
            entitiesLink: <TooltipLink href={API_DOCS_URLS.entities} />,
            batchLink: <TooltipLink href={API_DOCS_URLS.entitiesBatch} />,
          }}
        />
      }
      icon={<LeaderboardOutlinedIcon />}
      filtersTitle={t('entity_statistics.filters')}
      onResetFilters={entitiesStats.resetFilters}
      isLoading={entitiesStats.isLoading}
      loadingText={t('entity_statistics.entities_loading')}
      isEmpty={entitiesStats.results.length === 0}
      emptyText={t('entity_statistics.entities_empty')}
      filters={
        <>
          <DateRangeFilter state={entitiesStats} />
          <ResultsCountFilter state={entitiesStats} />
          <EntityIdFilter state={entitiesStats} />
          <OriginFilter
            state={entitiesStats}
            options={AVAILABLE_STATISTICS_ORIGINS}
            labelKeyPrefix="entity_statistics.origin_"
          />
        </>
      }
    >
      <StatisticsRankedList
        valueLabel={t('entity_statistics.returned_count')}
        actionTooltip={t('entity_statistics.open_in_daily_control')}
        entries={entitiesStats.results.map((entry, index) => ({
          id: `${entry.entity}-${index}`,
          label: formatEntityLabel(entry.entity, entry.name),
          value: entry.returned_count,
          href: `https://dailycontrol.com/screening/${entry.entity}`,
        }))}
      />
    </StatisticsSection>
  );
};

export default EntitiesTab;
